/**
 * Sprite resolution using local /assets/sprites/ directory.
 * Falls back gracefully when sprite files are not yet present.
 *
 * Format resolution policy (for animated sprites):
 *   1. .gif
 *   2. .apng
 *   3. .png (current APNG files also work natively)
 *   4. Raw frame sequence: <id>_1.<ext>, <id>_2.<ext>, ... (.png/.jpg/.jpeg)
 */

import {
  MAX_RAW_FRAMES,
  SPRITE_FORMAT_ORDER,
  SPRITE_FRAME_INTERVAL_MS,
} from "../config/constants";
import { gen } from "./gen5";

const SPRITE_BASE = "/assets/sprites";

let speciesCache: Map<string, number> | null = null;

export type SpriteFormat = (typeof SPRITE_FORMAT_ORDER)[number];

export type SpriteResolution =
  | { kind: "native"; url: string }
  | { kind: "frames"; urls: string[]; intervalMs: number };

export function getDexNumber(species: string): number | null {
  if (speciesCache?.has(species)) {
    return speciesCache.get(species)!;
  }
  const s = gen.species.get(species);
  if (s?.num) {
    if (!speciesCache) speciesCache = new Map();
    speciesCache.set(species, s.num);
    return s.num;
  }
  return null;
}

/** Test whether an image URL can be loaded (file exists). */
export function testImageExists(url: string): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function buildUrl(dir: string, name: string, ext: string): string {
  return `${SPRITE_BASE}/${dir}/${name}.${ext}`;
}

function nativeSpriteUrl(dir: string, name: string, ext: string): string {
  return buildUrl(dir, name, ext);
}

function isRawFrameSequence(url: string): boolean {
  return /_\d+\.(png|jpg|jpeg)$/i.test(url);
}

async function probeFormatOrder(
  dir: string,
  name: string,
  formats: SpriteFormat[],
): Promise<SpriteResolution | null> {
  for (const ext of formats) {
    const url = nativeSpriteUrl(dir, name, ext);
    if (await testImageExists(url)) {
      return { kind: "native", url };
    }
  }
  return null;
}

async function probeRawFrames(
  dir: string,
  name: string,
  formats: SpriteFormat[],
): Promise<SpriteResolution | null> {
  for (const ext of formats) {
    const urls: string[] = [];
    for (let i = 1; i <= MAX_RAW_FRAMES; i++) {
      const url = buildUrl(dir, `${name}_${i}`, ext);
      if (await testImageExists(url)) {
        urls.push(url);
      } else {
        break;
      }
    }
    if (urls.length > 0) {
      return { kind: "frames", urls, intervalMs: SPRITE_FRAME_INTERVAL_MS };
    }
  }
  return null;
}

async function resolveSprite(
  dir: string,
  name: string,
  preferAnimated: boolean,
): Promise<SpriteResolution | null> {
  const order: SpriteFormat[] = preferAnimated
    ? [...SPRITE_FORMAT_ORDER]
    : ["png", "jpg", "jpeg"];

  const native = await probeFormatOrder(dir, name, order);
  if (native) return native;

  // Only probe raw frame sequences when animated output is desired.
  if (preferAnimated) {
    const frames = await probeRawFrames(dir, name, order);
    if (frames) return frames;
  }

  return null;
}

/** Resolve the best available front sprite for a Pokemon. */
export async function resolvePokemonFrontSprite(
  species: string,
  shiny = false,
  preferAnimated = true,
): Promise<SpriteResolution | null> {
  const dex = getDexNumber(species);
  if (dex === null) return null;
  const name = `${dex}${shiny ? "_shiny" : ""}`;
  return resolveSprite("pokemon/front", name, preferAnimated);
}

/** Resolve the best available back sprite for a Pokemon. */
export async function resolvePokemonBackSprite(
  species: string,
  shiny = false,
  preferAnimated = true,
): Promise<SpriteResolution | null> {
  const dex = getDexNumber(species);
  if (dex === null) return null;
  const name = `${dex}${shiny ? "_shiny" : ""}`;
  return resolveSprite("pokemon/back", name, preferAnimated);
}

/** Resolve the best available trainer sprite. */
export async function resolveTrainerSprite(
  kind: "front" | "back" | "vs",
  id: string,
  preferAnimated = true,
): Promise<SpriteResolution | null> {
  return resolveSprite(`trainers/${kind}`, id, preferAnimated);
}

export function pokemonFrontUrl(species: string, shiny = false): string {
  const dex = getDexNumber(species);
  if (dex === null) return "";
  const shinyPart = shiny ? "_shiny" : "";
  return `${SPRITE_BASE}/pokemon/front/${dex}${shinyPart}.png`;
}

export function pokemonBackUrl(species: string, shiny = false): string {
  const dex = getDexNumber(species);
  if (dex === null) return "";
  const shinyPart = shiny ? "_shiny" : "";
  return `${SPRITE_BASE}/pokemon/back/${dex}${shinyPart}.png`;
}

export function trainerFrontUrl(id: string): string {
  return `${SPRITE_BASE}/trainers/front/${id}.png`;
}

export function trainerBackUrl(id: string): string {
  return `${SPRITE_BASE}/trainers/back/${id}.png`;
}

export function trainerVsUrl(id: string): string {
  return `${SPRITE_BASE}/trainers/vs/${id}.png`;
}

export function pokemonMenuUrl(species: string): string {
  const dex = getDexNumber(species);
  if (dex === null) return "";
  return `${SPRITE_BASE}/pokemon/menu/${dex}_menu.png`;
}

export function backgroundImageUrl(id: string): string {
  return `${SPRITE_BASE}/backgrounds/${id}.png`;
}

export { isRawFrameSequence };
